import {
  Box,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  DialogTitle,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LinkIcon from "@mui/icons-material/Link";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import useConfig from "../../../hooks/useConfig";
import React from "react";
import ReplyIcon from "@mui/icons-material/Reply";
import ShareIcon from "@mui/icons-material/Share";
import CopyIcon from "@mui/icons-material/CopyAll";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
interface SocialShareProps {
  url: string;
  title: string;
}

const SocialShare = ({ url, title }: SocialShareProps) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const configData = useConfig();
  const [open, setOpen] = React.useState(false);
  const shareLinks = {
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        sx={{
          borderRadius: "50px",
          "&:hover": { bgcolor: "transparent", color: configData.primary },
        }}
        startIcon={<ReplyIcon />}
        onClick={() => setOpen(true)}
      >
        Share
      </Button>
      <Dialog maxWidth="xs" fullWidth open={open} onClose={handleClose}>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ color: "black" }}>
              Share
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Tooltip title="Share on WhatsApp" arrow placement="top">
              <IconButton
                onClick={() => window.open(shareLinks.whatsapp, "_blank")}
                sx={{
                  bgcolor: configData.primary,
                  color: "white",
                  "&:hover": { bgcolor: configData.primary },
                }}
              >
                <WhatsAppIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Share on Facebook" arrow placement="top">
              <IconButton
                onClick={() => window.open(shareLinks.facebook, "_blank")}
                sx={{
                  bgcolor: configData.primary,
                  color: "white",
                  "&:hover": { bgcolor: configData.primary },
                }}
              >
                <FacebookIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Share on X" arrow placement="top">
              <IconButton
                onClick={() => window.open(shareLinks.twitter, "_blank")}
                sx={{
                  bgcolor: configData.primary,
                  color: "white",
                  "&:hover": { bgcolor: configData.primary },
                }}
              >
                <FontAwesomeIcon icon={faXTwitter as IconProp} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Share on LinkedIn" arrow placement="top">
              <IconButton
                onClick={() => window.open(shareLinks.linkedin, "_blank")}
                sx={{
                  bgcolor: configData.primary,
                  color: "white",
                  "&:hover": { bgcolor: configData.primary },
                }}
              >
                <LinkedInIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Copy Link" arrow placement="top">
              <IconButton
                onClick={copyToClipboard}
                sx={{
                  bgcolor: configData.primary,
                  color: "white",
                  "&:hover": { bgcolor: configData.primary },
                }}
              >
                <LinkIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <TextField
            sx={{
              mt: 3,
              "& .MuiOutlinedInput-root": { borderRadius: "50px" },
            }}
            value={url}
            variant="outlined"
            fullWidth
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    sx={{ borderRadius: "50px" }}
                    startIcon={<ContentCopyIcon />}
                    onClick={copyToClipboard}
                  >
                    Copy
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

interface MetaTagsProps {
  title: string;
  description: string;
  image: string;
  url: string;
}

export const MetaTags = ({ title, description, image, url }: MetaTagsProps) => {
  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Open Graph meta tags (Facebook, WhatsApp, etc.) */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="video.other" />

      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SocialShare;
